import { useEffect, useMemo } from 'react'
import { Autocomplete, TextField } from '@mui/material'
import { comparisonOperatorConfigs, Operator } from '@global/constants'
import { User } from '@global/types'
import { t } from '@lingui/core/macro'

interface Props {
  filterIndex: number
  filterValue: User[]
  options: User[]
  operator: Operator
  onChange: (idx: number) => (obj: { target: { value: (string | User)[] } }) => void
}

export const SelectMultipleUsers = ({
  filterIndex,
  filterValue,
  operator,
  options,
  onChange,
}: Props) => {
  const operatorConfig = comparisonOperatorConfigs[operator]
  const canHaveMultipleValues = Boolean(operatorConfig?.canHaveMultipleValues())

  useEffect(() => {
    if (!canHaveMultipleValues) {
      onChange(filterIndex)({ target: { value: filterValue } })
    }
  }, [canHaveMultipleValues, filterValue])

  const value = useMemo(() => {
    if (Array.isArray(filterValue)) {
      return filterValue
    }

    if (filterValue === '') {
      return []
    }

    if (typeof filterValue === 'object') {
      return [filterValue]
    }
  }, [filterValue])

  return (
    <Autocomplete
      data-test-id="select-users"
      multiple
      fullWidth
      options={options}
      filterSelectedOptions
      disableCloseOnSelect
      getOptionLabel={option => option.name}
      value={value}
      onChange={(_, newValues) => {
        handleChange(newValues, filterIndex)
      }}
      isOptionEqualToValue={(opt, value) => opt.id === value.id}
      limitTags={1}
      disableListWrap
      renderTags={users => users.map(user => user.name).join(', ')}
      renderInput={params => <TextField {...params} variant="standard" label={t`Values`} />}
    />
  )

  function handleChange(values: User[], index: number): void {
    onChange(index)({ target: { value: values } })
  }
}
